import {
    Link,
    Table,
    TableCell,
    TableContainer,
    TableRow,
    createStyles,
    makeStyles,
} from '@material-ui/core';
import { useEffect, useState } from 'react';
import { SortableTableBody } from '../../../reusable/sortableTableBody';
import { HeadCell, Order, SortableTableHeader } from '../../../reusable/sortableTableHeader';
import { SettingsNavigation } from '../settingsNavigation';

import { SiteDto, getSites } from '../../../../api/siteApi';
import { ThresholdConditionDto, getAllThresholdConditions } from '../../../../api/thresholdConditionApi';
import { AccessLevels } from '../../../../auth/authAccessLevels';
import { checkUserPermission } from '../../../../auth/authChecker';
import { formatTypeForDisplay } from '../../../../enums/eventType';
import { CustomTheme } from '../../../../styles/theme';
import {
    ThresholdEventPopup, formatConditionForDisplay,
    formatFieldForDisplay,
    getUnitForField
} from './editThresholdEventPopup';

const useStyles = makeStyles((theme: CustomTheme) =>
    createStyles({
        thresholdEventsRoot: {
            boxShadow: '0px 2px 4px #FFFFFF29',
        },
        thresholdEventsContent: {
            padding: '1.5rem',
            paddingTop: '0.5rem',
        },
        table: {
            width: 'auto',
        },
        editLink: {
            color: theme.palette.primary.light,
        },
        tableBody: {
            maxHeight: 'calc(100vh - 240px)',
            overflowY: 'auto',
        },
    }),
);

export const ThresholdEventsPage = () => {
    const classes = useStyles();

    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<keyof ThresholdConditionDto>('siteName');
    const [siteList, setSiteList] = useState<SiteDto[]>([]);
    const [thresholdConditions, setThresholdConditions] = useState<ThresholdConditionDto[]>([]);
    const headCells: HeadCell<ThresholdConditionDto>[] = [
        { id: 'siteName', label: 'Site', isSortable: true, dataField: 'siteName' },
        { id: 'eventType', label: 'Event Type', isSortable: false },
        { id: 'field', label: 'Field', isSortable: false },
        { id: 'eventCondition', label: 'Condition', isSortable: false },
        { id: 'minimumDuration', label: 'Minimum Duration', isSortable: false },
        { id: 'edit', label: '', isSortable: false },
    ];
    const [isEditing, setIsEditing] = useState(false);
    const [isNewData, setIsNewData] = useState(false);
    const [infoForEdit, setInfoForEdit] = useState(
        thresholdConditions.length > 0 ? thresholdConditions[0] : null,
    );

    useEffect(() => {
        setIsNewData(true);
    }, []);

    useEffect(() => {
        if (isNewData) {
            const fetchThresholdConditions = async () => {
                const result = await getAllThresholdConditions();
                if (result.data) {
                    setThresholdConditions(result.data);
                }
                setIsNewData(false);
            };

            fetchThresholdConditions();
        }
    }, [isNewData]);

    useEffect(() => {
        const fetchSites = async () => {
            const result = await getSites();
            if (result.data) {
                setSiteList(result.data);
            }
        };
        fetchSites();
    }, []);

    const openEditEvent = (rowID: number) => {
        const site = thresholdConditions.find((e) => e.id === rowID);
        if (site) {
            setInfoForEdit(site);
            setIsEditing(true);
        } else {
            console.log('Error: site not found');
        }
    };

    const openNewEvent = () => {
        setInfoForEdit(null);
        setIsEditing(true);
    };

    const handleClose = (newData: boolean) => {
        setIsEditing(false);
        setIsNewData(newData);
    };

    return (
        <>
            <div className={classes.thresholdEventsRoot}>
                <SettingsNavigation
                    addText={'Add New Condition'}
                    addAction={() => openNewEvent()}
                />
                <div className={classes.thresholdEventsContent}>
                    <TableContainer className={classes.tableBody}>
                        <Table className={classes.table} aria-label="simple table">
                            <SortableTableHeader
                                order={order}
                                orderBy={orderBy}
                                headCells={headCells}
                                setOrder={setOrder}
                                setOrderBy={setOrderBy}
                            />
                            <SortableTableBody
                                order={order}
                                orderBy={orderBy}
                                data={thresholdConditions}
                                renderRow={(row: ThresholdConditionDto) => {
                                    return (
                                        <TableRow hover key={row.id}>
                                            <TableCell padding="none">{row.siteName}</TableCell>
                                            <TableCell>
                                                {formatTypeForDisplay(row.eventType)}
                                            </TableCell>
                                            <TableCell>
                                                {formatFieldForDisplay(row.field, true)}
                                            </TableCell>
                                            <TableCell>
                                                {formatConditionForDisplay(
                                                    row.eventCondition,
                                                    row.value,
                                                ) + getUnitForField(row.field)}
                                            </TableCell>
                                            <TableCell>{row.minimumDuration} hours</TableCell>
                                            {checkUserPermission(AccessLevels.Administrator) && (
                                                <TableCell>
                                                    <Link
                                                        component="button"
                                                        className={classes.editLink}
                                                        onClick={() => openEditEvent(row.id)}
                                                    >
                                                        Edit
                                                    </Link>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                }}
                            />
                        </Table>
                    </TableContainer>
                </div>
            </div>
            {isEditing && checkUserPermission(AccessLevels.Administrator) ? (
                <ThresholdEventPopup
                    currentData={infoForEdit}
                    handleClose={(newData: boolean) => handleClose(newData)}
                    siteList={siteList}
                />
            ) : null}
        </>
    );
};
